import React, { ReactNode, useEffect } from 'react'
import {
  OButton,
  OCol,
  ORow,
  OWizard,
  OWizardStep
} from '@dnvgl-onefoundation/onedesign-react'
import { Link } from 'react-router-dom'
import { Page, ContentSection } from '@/components/layout'
import {
  SelectProvidersStep,
  DefineScopeStep,
  ChooseProjectStep,
  SummaryStep
} from '@/components/planning'
import {
  BreadcrumbsTitle,
  PageNotFound,
  SaveButton
} from '@/components/helpers'
import { config, helper } from '@/utils'
import {
  useCountries,
  useProviders,
  useScenario,
  useCircularityRateData,
  useScenarioStepsAvailability,
  useSelectProvidersStorage
} from '@/hooks'
import AdminUpdateCalculationResults from '@/components/admin/calculation/AdminUpdateCalculationResults'
import { AppUser } from '@/interfaces'

const { urls, icons } = config
const { isSuperAdministrator } = helper

interface Props {
  currentUser: AppUser | null
}

interface PlanningWizardStepProps {
  title?: string
  actions?: ReactNode
  children?: ReactNode
}

const PlanningWizardStep = ({
  title,
  actions,
  children
}: PlanningWizardStepProps) => (
  <ORow className="pt-0 px-4">
    <OCol md={actions ? '8' : '12'}>
      <h2>{title}</h2>
    </OCol>
    {!!actions && (
      <OCol md="4" className="text-right mt-3">
        {actions}
      </OCol>
    )}
    <OCol md="12" className="wizard-step-content">
      {children}
    </OCol>
  </ORow>
)

const ScenarioPage = ({ currentUser }: Props) => {
  const {
    isLoading,
    isNotFound,
    scenarioId,
    scenario,
    projectItems,
    isLoadingProjects,
    selectedProject,
    selectedStep,
    turbineMaterialBreakdown,
    onUpdateSelectedProject,
    onRemove,
    onRename,
    onStepChanged,
    onSave,
    onSaveToDrafts,
    onSaveAndClose
  } = useScenario()

  const {
    toggleRequiredProvider,
    isMetalProcessingProviderRequired,
    isBladeProcessingProviderIdRequired,
    isConcreteProcessingProviderRequired
  } = useSelectProvidersStorage(scenario)

  const { countryOptions } = useCountries()
  const { filteredItems } = useProviders()
  const {
    turbineCircularityRateForScenario,
    getTurbineCircularityRateDataForScenario
  } = useCircularityRateData()

  const {
    isProvidersStepDisabled,
    isDefineScopeStepDisabled,
    isSummaryStepDisabled
  } = useScenarioStepsAvailability(scenario)

  useEffect(() => {
    if (scenario?.projectId)
      getTurbineCircularityRateDataForScenario(scenarioId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject])

  return (
    <Page
      title={
        <BreadcrumbsTitle
          disabled={isLoading}
          isDraft={scenario?.isDraft}
          title={scenario?.name}
          backLink={urls.scenarios}
          backName="Scenarios"
        />
      }
      isH1Title={false}
      actions={
        !isLoading &&
        !isNotFound && (
          <div className="mt-2">
            <Link to={[urls.settingsScenarios, scenario?.id].join('/')}>
              <OButton
                iconClass={icons.calculator}
                variant="flat"
                disabled={isLoading}>
                Open settings
              </OButton>
            </Link>
            {!!scenario?.id && (
              <OButton
                onClick={onRename}
                iconClass="fas fa-pen"
                variant="flat"
                disabled={isLoading}>
                Rename
              </OButton>
            )}
            {isSuperAdministrator(currentUser) &&
              selectedProject?.projectType !== undefined && (
                <AdminUpdateCalculationResults
                  projectType={selectedProject.projectType}
                  scenario={scenario}
                />
              )}
            {!!scenario?.projectId && (
              <Link to={`${urls.reports}/${scenario?.id}`}>
                <OButton className="mr-1">Full report</OButton>
              </Link>
            )}
            {!!scenario?.id && (
              <SaveButton
                onSave={onSave}
                onSaveAndClose={onSaveAndClose}
                onSaveToDrafts={onSaveToDrafts}
              />
            )}
          </div>
        )
      }>
      {isNotFound ? (
        <ORow>
          <PageNotFound />
        </ORow>
      ) : (
        <ContentSection>
          <OWizard
            className="overflow-hidden btnFinish-hidden"
            fullPageWizard={false}
            selectedStep={selectedStep}
            btnCancelText={
              <div className="btn btn-flat btn-sm">
                <i className="fas fa-trash text-danger mr-2" />
                {scenario?.isDraft ? 'Discard draft' : 'Remove scenario'}
              </div>
            }
            onCancelled={onRemove}
            onStepChanged={onStepChanged}>
            <OWizardStep title="Choose project">
              <PlanningWizardStep title="Choose project">
                {!!projectItems?.length && (
                  <ChooseProjectStep
                    items={projectItems}
                    isLoading={isLoadingProjects}
                    isChangingProjectDisabled={
                      !!(
                        scenario?.metalProcessingProviderId ||
                        scenario?.bladeProcessingProviderId ||
                        scenario?.concreteProcessingProviderId
                      )
                    }
                    selectedProject={selectedProject}
                    countryOptions={countryOptions}
                    onUpdateSelectedProject={onUpdateSelectedProject}
                  />
                )}
              </PlanningWizardStep>
            </OWizardStep>
            <OWizardStep
              title="Select providers"
              disabled={isProvidersStepDisabled}>
              <PlanningWizardStep title="Select providers">
                <SelectProvidersStep
                  isMetalProcessingProviderRequired={
                    isMetalProcessingProviderRequired
                  }
                  isBladeProcessingProviderIdRequired={
                    isBladeProcessingProviderIdRequired
                  }
                  isConcreteProcessingProviderRequired={
                    isConcreteProcessingProviderRequired
                  }
                  isNextStepDisabled={isDefineScopeStepDisabled}
                  toggleRequiredProvider={toggleRequiredProvider}
                />
              </PlanningWizardStep>
            </OWizardStep>
            <OWizardStep
              title="Define scope"
              disabled={isDefineScopeStepDisabled}>
              <PlanningWizardStep>
                <DefineScopeStep
                  project={selectedProject}
                  scenario={scenario}
                  isSuperAdministrator={isSuperAdministrator(currentUser)}
                />
              </PlanningWizardStep>
            </OWizardStep>
            <OWizardStep title="Summary" disabled={isSummaryStepDisabled}>
              <PlanningWizardStep title="Summary">
                <SummaryStep
                  selectedProject={selectedProject}
                  scenario={scenario}
                  turbineMaterialBreakdown={turbineMaterialBreakdown}
                  providerItems={filteredItems}
                  turbineCircularityRateForScenario={
                    turbineCircularityRateForScenario
                  }
                />
              </PlanningWizardStep>
            </OWizardStep>
          </OWizard>
        </ContentSection>
      )}
    </Page>
  )
}

export default React.memo(ScenarioPage)
